// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { Leaf } from 'lucide-react'
import { useState } from 'react'
import { Outlet } from 'react-router-dom'
import { Sidebar }     from '@/components/navigation/Sidebar'
import { TopBar }      from '@/components/navigation/TopBar'
import { BottomTabBar } from '@/components/navigation/BottomTabBar'
import { ErrorBoundary } from '@/components/feedback/ErrorBoundary'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { cn } from '@/lib/utils'

export function AppShell() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { isMobile, isTablet } = useBreakpoint()

  return (
    <div className="flex w-full overflow-hidden bg-sand" style={{ height: '100dvh' }}>
      {/* Sidebar - desktop only */}
      {!isMobile && (
        <Sidebar collapsed={isTablet || sidebarCollapsed} />
      )}

      {/* Main column */}
      <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <TopBar
          showMenu={isMobile}
          onMenuToggle={() =>
            isMobile ? setMobileMenuOpen(true) : setSidebarCollapsed((c) => !c)
          }
        />

        {/* Scrollable content */}
        <main
          id="main-content"
          className="flex-1 overflow-y-auto"
        >
          <div
            className={cn(
              'mx-auto w-full',
              isMobile ? 'px-4 py-5' : 'max-w-content px-8 py-8',
            )}
          >
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>

            {/* ── Universele footer ─────────────────────────────────── */}
            <footer className="mt-10 pt-6 border-t border-sand-200">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-moss flex-shrink-0">
                    <Leaf className="h-3.5 w-3.5 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-ink">WilsApp</span>
                </div>
                <p className="text-xs text-ink-4 leading-relaxed">
                  Leg vandaag vast wat uw nabestaanden later nodig hebben.
                </p>
                <div className="flex flex-wrap items-center gap-3 mt-1">
                  <a href="/privacy" className="text-xs text-ink-4 hover:text-ink-3 transition-colors underline-offset-2 hover:underline">Privacy</a>
                  <span className="text-ink-4 text-xs">·</span>
                  <a href="/voorwaarden" className="text-xs text-ink-4 hover:text-ink-3 transition-colors underline-offset-2 hover:underline">Gebruiksvoorwaarden</a>
                  <span className="text-ink-4 text-xs">·</span>
                  <span className="text-xs text-ink-4">© WilsApp 2026</span>
                </div>
              </div>
            </footer>
          </div>
        </main>

        {/* Bottom tab bar - mobile only */}
        {isMobile && <BottomTabBar />}
      </div>

      {/* Mobile menu drawer - opent via hamburger linksboven */}
      {isMobile && mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-ink/40 animate-fade-in"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
          <div
            className="absolute inset-y-0 left-0 max-w-[85%] shadow-xl"
            style={{ paddingTop: 'env(safe-area-inset-top)' }}
          >
            <Sidebar collapsed={false} onNavigate={() => setMobileMenuOpen(false)} />
          </div>
        </div>
      )}
    </div>
  )
}
